import React, { useState, cloneElement } from 'react';
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import { Users, ShoppingBag, DollarSign, BarChart3, Settings, Star, Zap, MessageSquare, Crown, CalendarDays, Layers, TrendingUp, LogOut, FileText, LayoutGrid, Target, Megaphone, Store, LayoutDashboard, Video, Calendar, CreditCard, Menu, X, Package, ChevronRight } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useUser } from '../contexts/UserContext';
import { useLanguage } from '../i18n/LanguageContext';
// Import all tabs (existing and new)
import { OverviewTab } from '../components/astrologer-dashboard/OverviewTab';
import { OrdersTab } from '../components/astrologer-dashboard/OrdersTab';
import { ConsultationsTab } from '../components/astrologer-dashboard/ConsultationsTab';
import { ChatTab } from '../components/astrologer-dashboard/ChatTab';
import { ServicesTab } from '../components/astrologer-dashboard/ServicesTab';
import { MarketplaceTab } from '../components/astrologer-dashboard/MarketplaceTab';
import { CalendarScheduleTab } from '../components/astrologer-dashboard/CalendarScheduleTab';
import { FinancesTab } from '../components/astrologer-dashboard/FinancesTab';
import { PromotionTab } from '../components/astrologer-dashboard/PromotionTab';
import { ReviewsTab } from '../components/astrologer-dashboard/ReviewsTab';
import { StatsTab } from '../components/astrologer-dashboard/StatsTab';
import { ProfileTab } from '../components/astrologer-dashboard/ProfileTab';
import { SettingsSecurityTab } from '../components/astrologer-dashboard/SettingsSecurityTab';
import { SubscriptionTab } from '../components/astrologer-dashboard/SubscriptionTab';
import { PackagesTab } from '../components/astrologer-dashboard/PackagesTab';
import { AutoServiceDetail } from '../components/astrologer-dashboard/AutoServiceDetail';
interface AstrologerDashboardPageProps {
  onNavigate: (page: string) => void;
}
export function AstrologerDashboardPage({
  onNavigate
}: AstrologerDashboardPageProps) {
  const {
    user,
    logout
  } = useUser();
  const {
    t
  } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [activeAutoService, setActiveAutoService] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };
  // 16-section sidebar grouped logically
  const menuGroups = [{
    title: t('astrodash.group.main'),
    items: [{
      id: 'overview',
      label: t('astrodash.tab.overview'),
      icon: <LayoutDashboard className="w-5 h-5" data-id="element-21484" />
    }, {
      id: 'orders',
      label: t('astrodash.tab.orders'),
      icon: <ShoppingBag className="w-5 h-5" data-id="element-21485" />
    }, {
      id: 'consultations',
      label: t('astrodash.tab.consultations'),
      icon: <Video className="w-5 h-5" data-id="element-21486" />
    }, {
      id: 'chat',
      label: t('astrodash.tab.chat'),
      icon: <MessageSquare className="w-5 h-5" data-id="element-21487" />
    }]
  }, {
    title: t('astrodash.group.products'),
    items: [{
      id: 'services',
      label: t('astrodash.tab.services'),
      icon: <Layers className="w-5 h-5" data-id="element-21488" />
    }, {
      id: 'marketplace',
      label: t('astrodash.tab.marketplace'),
      icon: <Store className="w-5 h-5" data-id="element-21489" />
    }, {
      id: 'calendar',
      label: t('astrodash.tab.calendar'),
      icon: <Calendar className="w-5 h-5" data-id="element-21490" />
    }]
  }, {
    title: t('astrodash.group.business'),
    items: [{
      id: 'finances',
      label: t('astrodash.tab.finances'),
      icon: <DollarSign className="w-5 h-5" data-id="element-21491" />
    }, {
      id: 'promotion',
      label: t('astrodash.tab.promotion'),
      icon: <Zap className="w-5 h-5" data-id="element-21492" />
    }, {
      id: 'reviews',
      label: t('astrodash.tab.reviews'),
      icon: <Star className="w-5 h-5" data-id="element-21493" />
    }, {
      id: 'stats',
      label: t('astrodash.tab.stats'),
      icon: <BarChart3 className="w-5 h-5" data-id="element-21494" />
    }]
  }, {
    title: t('astrodash.group.settings'),
    items: [{
      id: 'profile',
      label: t('astrodash.tab.profile'),
      icon: <Users className="w-5 h-5" data-id="element-21495" />
    }, {
      id: 'settings',
      label: t('astrodash.tab.settings'),
      icon: <Settings className="w-5 h-5" data-id="element-21496" />
    }, {
      id: 'subscription',
      label: t('astrodash.tab.subscription'),
      icon: <CreditCard className="w-5 h-5" data-id="element-21497" />
    }]
  }];
  const handleTabChange = (id: string) => {
    setActiveTab(id);
    setActiveAutoService(null);
    setMobileMenuOpen(false);
  };
  const renderContent = () => {
    if (activeAutoService) {
      return <AutoServiceDetail activeAutoService={activeAutoService} setActiveAutoService={setActiveAutoService} showToast={showToast} data-id="element-21498" />;
    }
    switch (activeTab) {
      case 'overview':
        return <OverviewTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-21499" />;
      case 'orders':
        return <OrdersTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-21500" />;
      case 'consultations':
        return <ConsultationsTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-21501" />;
      case 'chat':
        return <ChatTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-21502" />;
      case 'services':
        return <ServicesTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-21503" />;
      case 'marketplace':
        return <MarketplaceTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-21504" />;
      case 'calendar':
        return <CalendarScheduleTab showToast={showToast} data-id="element-21505" />;
      case 'finances':
        return <FinancesTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-21506" />;
      case 'promotion':
        return <PromotionTab showToast={showToast} setActiveAutoService={setActiveAutoService} setActiveTab={setActiveTab} data-id="element-21507" />;
      case 'reviews':
        return <ReviewsTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-21508" />;
      case 'stats':
        return <StatsTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-21509" />;
      case 'profile':
        return <ProfileTab showToast={showToast} onNavigate={onNavigate} setActiveTab={handleTabChange} data-id="element-21510" />;
      case 'settings':
        return <SettingsSecurityTab showToast={showToast} initialTab="general" data-id="element-21511" />;
      case 'subscription':
        return <SubscriptionTab showToast={showToast} data-id="element-21512" />;
      default:
        return <div className="flex flex-col items-center justify-center py-20 text-slate-500" data-id="element-21513">
            <Settings className="w-12 h-12 text-slate-300 mb-3" data-id="element-21514" />
            <p className="text-lg font-medium text-slate-900 mb-1" data-id="element-21515">
              {t('astrodash.common.noData')}
            </p>
            <p className="text-sm mb-4" data-id="element-21516">{t('astrodash.tab.overview')}</p>
            <Button variant="primary" size="sm" onClick={() => setActiveTab('overview')} data-id="element-21517">
              {t('astrodash.common.back')}
            </Button>
          </div>;
    }
  };
  // Get current tab label for mobile header
  const currentTabLabel = menuGroups.flatMap(g => g.items).find(item => item.id === activeTab)?.label || t('astrodash.tab.overview');
  const currentTabIcon = menuGroups.flatMap(g => g.items).find(item => item.id === activeTab)?.icon || <LayoutDashboard className="w-5 h-5" data-id="element-21518" />;
  // Bottom nav items (5 most used)
  const bottomNavItems = [{
    id: 'overview',
    label: 'Обзор',
    icon: <LayoutDashboard className="w-5 h-5" data-id="element-21519" />
  }, {
    id: 'orders',
    label: 'Заказы',
    icon: <ShoppingBag className="w-5 h-5" data-id="element-21520" />
  }, {
    id: 'chat',
    label: 'Чат',
    icon: <MessageSquare className="w-5 h-5" data-id="element-21521" />
  }, {
    id: 'finances',
    label: 'Финансы',
    icon: <DollarSign className="w-5 h-5" data-id="element-21522" />
  }, {
    id: 'stats',
    label: 'Статистика',
    icon: <BarChart3 className="w-5 h-5" data-id="element-21523" />
  }];
  return <div className="w-full bg-slate-50 min-h-screen pb-6 lg:pb-24" data-id="element-21524">
      {/* Mobile Bottom Nav Spacer */}
      <div className="h-0 lg:hidden" style={{
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }} data-id="element-21525" />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-4 lg:pt-12" data-id="element-21526">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between bg-white p-3 rounded-2xl border border-slate-200 shadow-sm mb-4" data-id="element-21527">
          <div className="flex items-center gap-3 min-w-0" data-id="element-21528">
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700 uppercase flex-shrink-0" data-id="element-21529">
              {user?.name?.slice(0, 2) || 'АС'}
            </div>
            <div className="min-w-0" data-id="element-21530">
              <div className="font-bold text-slate-900 text-sm truncate" data-id="element-21531">
                {currentTabLabel}
              </div>
              <div className="flex items-center gap-1.5" data-id="element-21532">
                <Badge variant="pro" size="sm" data-id="element-21533">
                  <Crown className="w-3 h-3 mr-0.5" data-id="element-21534" /> PRO
                </Badge>
              </div>
            </div>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-600 transition-colors flex-shrink-0" data-id="element-21535">
            <Menu className="w-5 h-5" data-id="element-21536" />
          </button>
        </div>

        {/* Mobile Sidebar Overlay */}
        {mobileMenuOpen && <div className="fixed inset-0 z-50 lg:hidden" data-id="element-21537">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} data-id="element-21538" />
            {/* Sidebar Panel */}
            <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-white shadow-2xl overflow-hidden animate-in slide-in-from-right duration-200 flex flex-col" data-id="element-21539">
              {/* Mobile Menu Header */}
              <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between flex-shrink-0" data-id="element-21540">
                <div className="flex items-center gap-3" data-id="element-21541">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-sm font-bold text-indigo-700 uppercase" data-id="element-21542">
                    {user?.name?.slice(0, 2) || 'АС'}
                  </div>
                  <div data-id="element-21543">
                    <div className="font-bold text-slate-900 text-sm" data-id="element-21544">
                      {user?.name || t('astrodash.role')}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5" data-id="element-21545">
                      <Badge variant="pro" size="sm" data-id="element-21546">
                        <Crown className="w-3 h-3 mr-0.5" data-id="element-21547" /> PRO{' '}
                        {t('astrodash.promo.tariff')}
                      </Badge>
                    </div>
                  </div>
                </div>
                <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 transition-colors" data-id="element-21548">
                  <X className="w-5 h-5" data-id="element-21549" />
                </button>
              </div>

              {/* Navigation */}
              <nav className="flex-1 overflow-y-auto p-3" data-id="element-21550">
                {menuGroups.map((group, idx) => <div key={idx} className="mb-4 last:mb-0" data-id="element-21551">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-3" data-id="element-21552">
                      {group.title}
                    </div>
                    {group.items.map(item => <button key={item.id} onClick={() => handleTabChange(item.id)} className={`w-full flex items-center justify-between px-3 py-2.5 mb-0.5 rounded-xl text-sm font-medium transition-colors ${activeTab === item.id && !activeAutoService ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50 active:bg-slate-100'}`} data-id="element-21553">
                        <div className="flex items-center" data-id="element-21554">
                          <span className={`mr-3 shrink-0 ${activeTab === item.id && !activeAutoService ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-21555">
                            {item.icon}
                          </span>
                          <span data-id="element-21556">{item.label}</span>
                        </div>
                        {activeTab === item.id && !activeAutoService && <ChevronRight className="w-4 h-4 text-indigo-400" data-id="element-21557" />}
                      </button>)}
                  </div>)}
              </nav>

              {/* Logout */}
              <div className="p-3 border-t border-slate-100 flex-shrink-0" data-id="element-21558">
                <button onClick={() => {
              logout();
              onNavigate('home');
            }} className="w-full flex items-center px-3 py-2.5 rounded-xl text-sm font-medium text-rose-600 hover:bg-rose-50 active:bg-rose-100 transition-colors" data-id="element-21559">
                  <LogOut className="w-4 h-4 mr-3 text-rose-400" data-id="element-21560" />{' '}
                  {t('astrodash.logout')}
                </button>
              </div>
            </div>
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8" data-id="element-21561">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block lg:col-span-3" data-id="element-21562">
            <Card padding="none" className="sticky top-24 overflow-hidden border-slate-200 shadow-sm" data-id="element-21563">
              {/* Desktop User Info */}
              <div className="p-6 border-b border-slate-100 bg-slate-50/50" data-id="element-21564">
                <div className="flex flex-col items-center text-center" data-id="element-21565">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center text-2xl font-bold text-indigo-700 mb-3 uppercase" data-id="element-21566">
                    {user?.name?.slice(0, 2) || 'АС'}
                  </div>
                  <div className="font-bold text-slate-900 text-lg mb-2" data-id="element-21567">
                    {user?.name || t('astrodash.role')}
                  </div>
                  <div className="w-full bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex flex-col items-center gap-2" data-id="element-21568">
                    <div className="flex items-center text-indigo-700 font-medium text-sm" data-id="element-21569">
                      <Crown className="w-4 h-4 mr-1.5" data-id="element-21570" /> PRO{' '}
                      {t('astrodash.promo.tariff')}
                    </div>
                    <Button variant="outline" size="sm" fullWidth className="text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-100/50 bg-white" onClick={() => handleTabChange('subscription')} data-id="element-21571">
                      {t('astrodash.tab.subscription')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Desktop Navigation */}
              <nav className="p-3 h-[calc(100vh-300px)] overflow-y-auto custom-scrollbar" data-id="element-21572">
                {menuGroups.map((group, idx) => <div key={idx} className="mb-4 last:mb-0" data-id="element-21573">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-3" data-id="element-21574">
                      {group.title}
                    </div>
                    {group.items.map(item => <button key={item.id} onClick={() => handleTabChange(item.id)} title={item.label} className={`w-full flex items-center px-3 py-2 mb-0.5 rounded-lg text-sm font-medium transition-colors ${activeTab === item.id && !activeAutoService ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-21575">
                        <span className={`mr-3 shrink-0 ${activeTab === item.id && !activeAutoService ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-21576">
                          {item.icon}
                        </span>
                        <span className="truncate" data-id="element-21577">{item.label}</span>
                      </button>)}
                  </div>)}

                <div className="pt-4 mt-2 border-t border-slate-100" data-id="element-21578">
                  <button onClick={() => {
                  logout();
                  onNavigate('home');
                }} className="w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors" data-id="element-21579">
                    <LogOut className="w-4 h-4 mr-3 text-rose-400" data-id="element-21580" />{' '}
                    {t('astrodash.logout')}
                  </button>
                </div>
              </nav>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-9 pb-20 lg:pb-0" data-id="element-21581">
            <SectionErrorBoundary key={activeTab} sectionName={`astrologer-${activeTab}`} fallbackMinHeight="min-h-[400px]" data-id="element-21582">
              {renderContent()}
            </SectionErrorBoundary>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]" style={{
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }} data-id="element-21583">
        <div className="flex items-center justify-around px-1 py-1.5" data-id="element-21584">
          {bottomNavItems.map(item => {
          const isActive = activeTab === item.id && !activeAutoService;
          return <button key={item.id} onClick={() => handleTabChange(item.id)} className={`flex flex-col items-center justify-center py-1.5 px-2 rounded-xl min-w-[56px] transition-colors ${isActive ? 'text-indigo-600' : 'text-slate-400 active:text-slate-600'}`} data-id="element-21585">
                <span className={isActive ? 'text-indigo-600' : 'text-slate-400'} data-id="element-21586">
                  {cloneElement(item.icon, {
                className: 'w-5 h-5'
              })}
                </span>
                <span className={`text-[10px] mt-0.5 font-medium ${isActive ? 'text-indigo-600' : 'text-slate-500'}`} data-id="element-21587">
                  {item.label}
                </span>
                {isActive && <div className="w-4 h-0.5 bg-indigo-500 rounded-full mt-0.5" data-id="element-21588" />}
              </button>;
        })}
          <button onClick={() => setMobileMenuOpen(true)} className="flex flex-col items-center justify-center py-1.5 px-2 rounded-xl min-w-[56px] text-slate-400 active:text-slate-600 transition-colors" data-id="element-21589">
            <Menu className="w-5 h-5" data-id="element-21590" />
            <span className="text-[10px] mt-0.5 font-medium text-slate-500" data-id="element-21591">
              Ещё
            </span>
          </button>
        </div>
      </div>

      {/* Toast notification */}
      {toast && <div className="fixed bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg animate-[fadeIn_0.2s_ease-out]" data-id="element-21592">
          {toast}
        </div>}
    </div>;
}